import {otsBaseApi} from "../../ots-api";
import {Role} from "../../../model/role";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Roles", "RolePermissions", "PersonRoles"]});

export const roleApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllRoles: builder.query<
      Role[],
      void
    >({
      query: (criteria) => ({
        url: `/roles/all`,
        method: 'GET',
        body: criteria,
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Roles", id} as const)),
            {type: "Roles", id: "LIST"}
          ];
        } else {
          return [{type: "Roles", id: "LIST"}];
        }
      },
    }),
    getRolesByPerson: builder.query<
      Role[],
      string
    >({
      query: (arg) => ({
        url: `/roles/all`,
        method: 'GET',
        params: {
          personId: arg,
        }
      }),
      providesTags: (result, error, arg) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Roles", id} as const)),
            {type: "PersonRoles", id: arg}
          ];
        } else {
          return [{type: "PersonRoles", id: arg}];
        }
      },
    }),
    getRole: builder.query<Role, string>({
      query: (id) => ({
        url: `/roles/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "Roles", id}]
    }),
    createRole: builder.mutation<Role, Partial<Role>>({
      query: (mapping) => ({
        url: "/roles",
        method: "POST",
        body: mapping
      }),
      invalidatesTags: [{type: "Roles", id: "LIST"}]
    }),
    updateRole: builder.mutation<Role, Partial<Role>>({
      query: (mapping) => ({
        url: "/roles",
        method: "PUT",
        body: mapping,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Roles", id},
      ]
    }),
    deleteRole: builder.mutation<void, Partial<Role>>({
      query: (mapping) => ({
        url: "/roles",
        method: "DELETE",
        body: mapping
      }),
      invalidatesTags: (result, error, {id}) => [{type: "Roles", id}]
    }),
    addPermissionToRole: builder.mutation<void, { roleId: string, permissionId: string }>({
      query: (arg) => ({
        url: `/roles/${arg.roleId}/permissions`,
        method: "POST",
        params: {
          permissionId: arg.permissionId,
        }
      }),
      invalidatesTags: (result, error, {roleId}) => [{type: "RolePermissions", id: roleId}]
    }),
    removePermissionToRole: builder.mutation<void, { roleId: string, permissionId: string }>({
      query: (arg) => ({
        url: `/roles/${arg.roleId}/permissions/${arg.permissionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, {roleId}) => [{type: "RolePermissions", id: roleId}]
    }),
    setRolesForPerson: builder.mutation<void, { roleIds: string[], personId: string }>({
      query: (arg) => ({
        url: `/roles/persons/${arg.personId}`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json;",
        },
        body: JSON.stringify(arg.roleIds),
      }),
      invalidatesTags: (result, error, {personId}) => [{type: "PersonRoles", id: personId}]
    }),
  })
});

export const {
  useGetAllRolesQuery,
  useGetRolesByPersonQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useSetRolesForPersonMutation,
  useAddPermissionToRoleMutation,
  useRemovePermissionToRoleMutation,
} = roleApi;
